import React from "react";

export type SocialLinksProps = {
    className?: string;
    /** Size in pixels (both width & height). Default 24. */
    iconSize?: number;
    /** If true (default), icons render in white via currentColor. */
    white?: boolean;
    links: {
        linkedin?: string;
        github?: string;
        email?: string; // can be full mailto: or just the address
        facebook?: string;
        instagram?: string;
        website?: string; // music site or personal site
        spotify?: string;
    };
};

/**
 * SocialLinks – compact icon row that fits the portfolio theme.
 * - White icons by default (`white` prop) using `currentColor` so they inherit text color.
 * - Smooth hover animation (scale + lift) and focus ring.
 * - Accessible names for icon-only links (sr-only text + aria-label).
 * - Uses `rel="noopener noreferrer"` for external links opened with target _blank.
 */
export default function SocialLinks({ className = "", iconSize = 24, white = true, links }: SocialLinksProps) {
    type SocialKey = keyof SocialLinksProps["links"];
    const raw = [
        { key: "linkedin" as const, label: "LinkedIn", href: links.linkedin },
        { key: "github" as const, label: "GitHub", href: links.github },
        { key: "email" as const, label: "Email", href: normalizeEmail(links.email) },
        { key: "facebook" as const, label: "Facebook", href: links.facebook },
        { key: "instagram" as const, label: "Instagram", href: links.instagram },
        { key: "website" as const, label: "Website", href: links.website },
        { key: "spotify" as const, label: "Spotify", href: links.spotify },
    ] as const;

    const entries = raw.filter((e) => !!e.href) as Array<{ key: SocialKey; label: string; href: string }>;


    const baseColor = white ? "text-white" : "text-tx"; // text-tx comes from your theme tokens

    return (
        <nav aria-label="Social links" className={className}>
            <ul className={`flex flex-wrap items-center gap-3 ${baseColor}`}>
                {entries.map((e) => (
                    <li key={e.key}>
                        <SocialIconLink href={e.href!} label={e.label} size={iconSize} />
                    </li>
                ))}
            </ul>
            {/* Motion safety and hover animation refinements */}
            <style>{`
        .soc-link{ display:inline-flex; align-items:center; justify-content:center; border-radius:999px; padding:8px; border:1px solid var(--br); background:color-mix(in oklab, var(--su) 8%, transparent); }
        .soc-link svg{ width:1em; height:1em; }
        .soc-link{ transition: transform .2s ease, box-shadow .2s ease, background-color .2s ease, opacity .2s ease; will-change: transform; }
        .soc-link:hover, .soc-link:focus-visible{ transform: translateY(-2px) scale(1.08); box-shadow: 0 6px 18px rgba(0,0,0,.18); background: var(--su); }
        .soc-link{ opacity:.92; } .soc-link:hover{ opacity:1; }
        @media (prefers-reduced-motion: reduce){ .soc-link{ transition:none; transform:none !important; } }
      `}</style>
        </nav>
    );
}

function SocialIconLink({ href, label, size }: { href: string; label: string; size: number }) {
    const isMail = href.startsWith("mailto:");
    const isExternal = !isMail && /^https?:\/\//i.test(href);

    return (
        <a
            className="soc-link focus:outline-none focus-visible:ring-2 focus-visible:ring-ac/60"
            href={href}
            {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            aria-label={label}
            title={label}
            style={{ fontSize: `${size}px` }}
        >
            <Icon name={label} />
            <span className="sr-only">{label}</span>
        </a>
    );
}

/**
 * Simple brand-ish icons built with currentColor (white by default).
 * These are lightweight and avoid extra deps.
 */
function Icon({ name }: { name: string }) {
    switch (name) {
        case 'LinkedIn': return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M7.5 10.2v6.3M7.5 7.1h.01M11.6 16.5v-3.7a2.2 2.2 0 0 1 4.4 0v3.7" />
            </svg>
        );
        case 'GitHub': return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 .5a11.5 11.5 0 0 0-3.64 22.43c.58.1.8-.25.8-.56v-2.1c-3.27.72-3.96-1.58-3.96-1.58-.53-1.37-1.3-1.74-1.3-1.74-1.06-.72.08-.71.08-.71 1.17.08 1.78 1.2 1.78 1.2 1.04 1.79 2.73 1.27 3.4.97.1-.77.41-1.27.74-1.56-2.62-.3-5.38-1.31-5.38-5.82 0-1.29.46-2.34 1.2-3.17-.12-.3-.52-1.52.12-3.16 0 0 .98-.31 3.2 1.21a11 11 0 0 1 5.82 0c2.22-1.52 3.2-1.21 3.2-1.21.64 1.64.24 2.86.12 3.16.75.83 1.2 1.88 1.2 3.17 0 4.52-2.77 5.52-5.4 5.81.42.37.8 1.09.8 2.2v3.26c0 .31.22.66.8.55A11.5 11.5 0 0 0 12 .5Z" />
            </svg>
        );
        case 'Email': return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m4 7 7.3 5.2a2 2 0 0 0 2.4 0L21 7" />
            </svg>
        );
        case 'Facebook': return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M13.5 10.5V8.7c0-.9.6-1.1 1.1-1.1h1.5V5h-2.2C12 5 10.5 6.4 10.5 8.5v2H9v2.6h1.5V21h3V13.1h2L16 10.5h-2.5z" />
            </svg>
        );
        case 'Instagram': return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="4.4" />
                <circle cx="12" cy="12" r="3.8" />
                <circle cx="17.4" cy="6.6" r="1" fill="currentColor" stroke="none" />
            </svg>
        );
        case 'Website': return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
            </svg>
        );
        case 'Spotify': return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.6 14.5a.8.8 0 0 1-1.1.3c-3-1.8-6.8-2-11-.6a.8.8 0 1 1-.5-1.5c4.7-1.6 8.9-1.4 12.3.7.4.2.6.7.3 1.1Zm1-2.7a1 1 0 0 1-1.3.3c-3.4-2-8.6-2.5-12.6-1.1a1 1 0 0 1-.6-1.9c4.6-1.6 10.3-1 14.2 1.3.5.3.7.9.3 1.4ZM18 9a1.2 1.2 0 0 1-1.5.4c-3.9-2.3-9.9-2.5-13.5-1.1a1.2 1.2 0 1 1-.8-2.3C6.4 4.3 13.1 4.6 17.6 7c.6.3.8 1 .4 1.6Z" />
            </svg>
        );
        default:
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 12h16M12 4v16" />
                </svg>
            );
    }
}

function normalizeEmail(email?: string) {
    if (!email) return undefined;
    const trimmed = email.trim();
    if (!trimmed) return undefined;
    return trimmed.startsWith("mailto:") ? trimmed : `mailto:${trimmed}`;
}
